import React, { useMemo, useState } from 'react';
import { X, AlertTriangle, Loader2 } from 'lucide-react';
import { CustomTimePicker } from '../common/CustomTimePicker';
import api from '../../api/auth.api';
import { API_CONFIG } from '../../config/api.config';

// Lean DAY-LEVEL clock-in/clock-out change *request* modal for the employee My
// Time page. Keeps it dead simple: one day, just the day's clock-in (first entry)
// and clock-out (last entry) — no per-entry/job editing. Asphalt crews also get a
// "didn't take lunch" option. Everything submitted is a pending request the admin
// signs off on the Approve Time Cards page.

export interface DayRequest {
  dateLabel: string;         // e.g. "Mon, Jun 9"
  entryDate: string;         // EST date 'YYYY-MM-DD' (for lunch proposed_date)
  jobName?: string;
  clockInEntryId: number;    // first entry of the day
  clockInIso: string;
  clockOutEntryId: number;   // last entry of the day
  clockOutIso: string;
  lunchTaken?: boolean;
}

interface Props {
  day: DayRequest;
  weekStartDate: string;     // Friday 'YYYY-MM-DD'
  isAsphaltCrew: boolean;
  stats: { change_requests: number; forgot_clock_out: number } | null;
  language: 'en' | 'es';
  // When present, the modal edits an existing PENDING request: prefill from it,
  // and on save withdraw these ids first (replace), or withdraw alone (cancel).
  pending?: { clockInIso?: string | null; clockOutIso?: string | null; noLunch?: boolean; note?: string | null; ids: number[] } | null;
  onClose: () => void;
  onSubmitted: () => void;
}

const t = {
  en: {
    title: 'Request a Time Change',
    editTitle: 'Edit Your Request',
    subtitle: 'Ask your manager to fix this day. This is a request, not an edit.',
    save: 'Save Changes', withdraw: 'Withdraw request',
    clockIn: 'Clock In',
    clockOut: 'Clock Out',
    noLunch: "I didn't take lunch",
    noLunchHint: '30 minutes will be added back for this day.',
    note: 'Add a note (optional)',
    notePlaceholder: 'Explain what happened…',
    submit: 'Send Request',
    cancel: 'Cancel',
    noChanges: 'Change a time or check a box before sending.',
    failed: 'Failed to send request. Please try again.',
    warn: 'This year',
    requests: 'time-change requests',
    forgot: 'forgot to clock out',
    warnTail: 'Frequent changes are reviewed by management.',
  },
  es: {
    title: 'Solicitar Cambio de Hora',
    editTitle: 'Editar Su Solicitud',
    subtitle: 'Pida a su gerente que corrija este día. Es una solicitud, no una edición.',
    save: 'Guardar Cambios', withdraw: 'Retirar solicitud',
    clockIn: 'Entrada',
    clockOut: 'Salida',
    noLunch: 'No tomé almuerzo',
    noLunchHint: 'Se agregarán 30 minutos a este día.',
    note: 'Agregar una nota (opcional)',
    notePlaceholder: 'Explique qué pasó…',
    submit: 'Enviar Solicitud',
    cancel: 'Cancelar',
    noChanges: 'Cambie una hora o marque una casilla antes de enviar.',
    failed: 'No se pudo enviar la solicitud. Intente de nuevo.',
    warn: 'Este año',
    requests: 'solicitudes de cambio',
    forgot: 'olvidó marcar salida',
    warnTail: 'La gerencia revisa los cambios frecuentes.',
  },
};

const isoToHHMM = (iso?: string | null) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
};

export const RequestTimeChangeModal: React.FC<Props> = ({
  day, weekStartDate, isAsphaltCrew, stats, language, pending, onClose, onSubmitted,
}) => {
  const L = t[language];
  const isEdit = !!pending;
  const origIn = useMemo(() => isoToHHMM(day.clockInIso), [day.clockInIso]);
  const origOut = useMemo(() => isoToHHMM(day.clockOutIso), [day.clockOutIso]);

  // In edit mode, prefill with what was already submitted; otherwise the actual times.
  const [clockIn, setClockIn] = useState(pending?.clockInIso ? isoToHHMM(pending.clockInIso) : origIn);
  const [clockOut, setClockOut] = useState(pending?.clockOutIso ? isoToHHMM(pending.clockOutIso) : origOut);
  const [noLunch, setNoLunch] = useState(!!pending?.noLunch);
  const [note, setNote] = useState(pending?.note || '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const canNoLunch = isAsphaltCrew && !!day.lunchTaken;
  const offenderTotal = (stats?.change_requests || 0) + (stats?.forgot_clock_out || 0);
  const warnColor = offenderTotal >= 5 ? '#dc2626' : offenderTotal >= 3 ? '#d97706' : '#64748b';
  const warnBg = offenderTotal >= 5 ? '#fef2f2' : offenderTotal >= 3 ? '#fffbeb' : '#f1f5f9';

  const withdrawPending = async () => {
    if (!pending?.ids?.length) return;
    await api.post(API_CONFIG.ENDPOINTS.TIMECARD_REVIEW_WITHDRAW, { correction_ids: pending.ids });
  };

  const withdraw = async () => {
    setBusy(true);
    setError(null);
    try {
      await withdrawPending();
      onSubmitted();
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || L.failed);
    } finally {
      setBusy(false);
    }
  };

  const submit = async () => {
    const corrections: any[] = [];
    if (clockIn && clockIn !== origIn) {
      corrections.push({ entry_id: day.clockInEntryId, correction_type: 'wrong_clock_in', proposed_clock_in: clockIn, employee_notes: note });
    }
    if (clockOut && clockOut !== origOut) {
      corrections.push({ entry_id: day.clockOutEntryId, correction_type: 'wrong_clock_out', proposed_clock_out: clockOut, employee_notes: note });
    }
    if (canNoLunch && noLunch) {
      corrections.push({ entry_id: day.clockInEntryId, correction_type: 'did_not_take_lunch', proposed_date: (day.entryDate || '').split('T')[0], employee_notes: note });
    }
    // Edit + reverted everything back to the real times → treat Save as a withdraw.
    if (corrections.length === 0) {
      if (isEdit) return withdraw();
      setError(L.noChanges); return;
    }

    setBusy(true);
    setError(null);
    try {
      if (isEdit) await withdrawPending(); // replace the old pending request
      const res = await api.post(API_CONFIG.ENDPOINTS.TIMECARD_REVIEW_CORRECTIONS, {
        week_start_date: weekStartDate,
        corrections,
        timezone_offset_minutes: new Date().getTimezoneOffset(),
      });
      if (!res.data?.success) throw new Error(res.data?.error || L.failed);
      onSubmitted();
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || L.failed);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={overlay} onClick={onClose}>
      <div style={sheet} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '20px 20px 14px' }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 22, fontWeight: 800, color: '#1f2937' }}>{isEdit ? L.editTitle : L.title}</div>
            <div style={{ fontSize: 16, fontWeight: 700, color: '#4338ca', marginTop: 6 }}>{day.dateLabel}</div>
            {day.jobName && <div style={{ fontSize: 15, color: '#64748b', marginTop: 2 }}>{day.jobName}</div>}
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 6, color: '#94a3b8' }}>
            <X size={26} />
          </button>
        </div>

        <div style={{ padding: '0 20px 20px', overflowY: 'auto' }}>
          {/* Offender warning */}
          {stats && offenderTotal > 0 && (
            <div style={{ display: 'flex', gap: 10, background: warnBg, border: `1px solid ${warnColor}40`, borderRadius: 12, padding: '12px 14px', marginBottom: 18 }}>
              <AlertTriangle size={20} color={warnColor} style={{ flexShrink: 0, marginTop: 1 }} />
              <div style={{ fontSize: 15, color: warnColor, lineHeight: 1.4 }}>
                <strong>{L.warn}:</strong> {stats.change_requests} {L.requests} · {stats.forgot_clock_out} {L.forgot}. {L.warnTail}
              </div>
            </div>
          )}

          <div style={{ marginBottom: 18 }}>
            <CustomTimePicker value={clockIn} onChange={setClockIn} label={L.clockIn} />
          </div>
          <div style={{ marginBottom: 18 }}>
            <CustomTimePicker value={clockOut} onChange={setClockOut} label={L.clockOut} />
          </div>

          {canNoLunch && (
            <div
              onClick={() => setNoLunch((v) => !v)}
              style={{ display: 'flex', alignItems: 'center', gap: 14, padding: 16, borderRadius: 12, marginBottom: 18, cursor: 'pointer',
                background: noLunch ? '#dcfce7' : '#f8fafc', border: `2px solid ${noLunch ? '#16a34a' : '#e5e7eb'}` }}
            >
              <div style={{ width: 28, height: 28, borderRadius: 8, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
                background: noLunch ? '#16a34a' : '#fff', border: `2px solid ${noLunch ? '#16a34a' : '#cbd5e1'}`, color: '#fff', fontSize: 18, fontWeight: 800 }}>
                {noLunch ? '✓' : ''}
              </div>
              <div>
                <div style={{ fontSize: 17, fontWeight: 700, color: '#1f2937' }}>🍽️ {L.noLunch}</div>
                {noLunch && <div style={{ fontSize: 14, color: '#166534' }}>{L.noLunchHint}</div>}
              </div>
            </div>
          )}

          <label style={{ fontSize: 16, fontWeight: 700, color: '#374151', display: 'block', marginBottom: 8 }}>{L.note}</label>
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder={L.notePlaceholder}
            style={{ width: '100%', minHeight: 80, padding: 14, fontSize: 17, border: '1px solid #d1d5db', borderRadius: 10, boxSizing: 'border-box', resize: 'vertical' }}
          />

          {error && (
            <div style={{ marginTop: 14, background: '#fef2f2', border: '1px solid #fecaca', color: '#dc2626', borderRadius: 10, padding: 12, fontSize: 15 }}>{error}</div>
          )}
        </div>

        {/* Footer */}
        <div style={{ padding: 18, borderTop: '1px solid #f1f5f9' }}>
          <div style={{ display: 'flex', gap: 12 }}>
            <button onClick={onClose} disabled={busy} style={{ flex: 1, padding: 18, borderRadius: 12, border: '1px solid #e5e7eb', background: '#fff', color: '#475569', fontSize: 18, fontWeight: 700, cursor: 'pointer' }}>
              {L.cancel}
            </button>
            <button onClick={submit} disabled={busy} style={{ flex: 2, padding: 18, borderRadius: 12, border: 'none', background: '#4f46e5', color: '#fff', fontSize: 18, fontWeight: 800, cursor: busy ? 'default' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, opacity: busy ? 0.7 : 1 }}>
              {busy && <Loader2 size={18} className="spin" />} {isEdit ? L.save : L.submit}
            </button>
          </div>
          {isEdit && (
            <button onClick={withdraw} disabled={busy} style={{ width: '100%', marginTop: 10, padding: 14, borderRadius: 12, border: 'none', background: 'none', color: '#dc2626', fontSize: 16, fontWeight: 700, cursor: busy ? 'default' : 'pointer' }}>
              {L.withdraw}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

const overlay: React.CSSProperties = {
  position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 1000,
  display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
};
const sheet: React.CSSProperties = {
  background: '#fff', width: '100%', maxWidth: 480, maxHeight: '92vh',
  borderTopLeftRadius: 22, borderTopRightRadius: 22, display: 'flex', flexDirection: 'column',
  boxShadow: '0 -4px 24px rgba(0,0,0,0.2)',
};

export default RequestTimeChangeModal;
